/**
 * OIDC Authorization Code + PKCE for a public SPA client. Tokens are kept in sessionStorage
 * (per tab, cleared on close); the IdP session cookie makes re-authentication seamless when the
 * 15-minute access token expires. No passwords ever touch this app.
 *
 * Works with any standards-based provider (ADR-0025): endpoints come from the issuer's discovery
 * document, and the client id, scopes and how the API audience is requested are build settings:
 *   VITE_OIDC_ISSUER        issuer URL (local IdP by default)
 *   VITE_OIDC_CLIENT_ID     public client id registered for the console
 *   VITE_OIDC_SCOPE         e.g. "openid email profile api://<app-id>/access" (Entra)
 *   VITE_OIDC_RESOURCE      RFC 8707 resource (local IdP); empty to omit
 *   VITE_OIDC_AUDIENCE      Auth0 "audience" parameter; empty to omit
 *   VITE_OIDC_MFA_ACR       acr_values to request when the API asks for a second factor
 *
 * VITE_AUTH_MODE=google (ADR-0026): "Sign in with Google". Google's tokens cannot be used by the
 * API directly and its web clients need a secret, so the console sends the code and PKCE verifier
 * to the LumaCast API, which returns LumaCast session tokens (15-minute access token, rotating
 * refresh token). Owners and admins then add an authenticator-app code.
 *   VITE_GOOGLE_CLIENT_ID   the OAuth web client id from Google Cloud
 *   VITE_GOOGLE_ISSUER      https://accounts.google.com (the local IdP stands in for it in dev)
 */
const env = import.meta.env;
const ISSUER = (env.VITE_OIDC_ISSUER ?? 'http://localhost:4100').replace(/\/$/, '');
const CLIENT_ID = env.VITE_OIDC_CLIENT_ID ?? 'lumacast-console';
const SCOPE = env.VITE_OIDC_SCOPE ?? 'openid email profile';
const RESOURCE = env.VITE_OIDC_RESOURCE ?? 'urn:lumacast:api';
const AUDIENCE = env.VITE_OIDC_AUDIENCE ?? '';
const MFA_ACR = env.VITE_OIDC_MFA_ACR ?? 'urn:lumacast:acr:mfa';
const KEY = 'lumacast.session';
export const AUTH_MODE: 'oidc' | 'google' = env.VITE_AUTH_MODE === 'google' ? 'google' : 'oidc';
const GOOGLE_ISSUER = (env.VITE_GOOGLE_ISSUER ?? 'https://accounts.google.com').replace(/\/$/, '');
const GOOGLE_CLIENT_ID = env.VITE_GOOGLE_CLIENT_ID ?? '';
const API = env.VITE_API_URL ?? 'http://localhost:4000';
const SIGNED_OUT = 'lumacast.signedOut';
const HINT = 'lumacast.loginHint';

interface Session {
  accessToken: string;
  /** OIDC mode: the provider's ID token (logout hint). */
  idToken?: string;
  /** Google mode: the LumaCast refresh token. */
  refreshToken?: string;
  expiresAt: number;
  name: string | null;
  email: string | null;
}

interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

let discovery: Promise<Discovery> | null = null;
function endpoints(): Promise<Discovery> {
  discovery ??= fetch(`${AUTH_MODE === 'google' ? GOOGLE_ISSUER : ISSUER}/.well-known/openid-configuration`)
    .then((r) => {
      if (!r.ok) throw new Error(`Sign-in provider unavailable (HTTP ${r.status})`);
      return r.json() as Promise<Discovery>;
    })
    .catch((e: unknown) => {
      discovery = null;
      throw e;
    });
  return discovery;
}

const b64url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const random = (n = 32) => b64url(crypto.getRandomValues(new Uint8Array(n)));
const audienceParams = (): Record<string, string> => ({ ...(RESOURCE ? { resource: RESOURCE } : {}), ...(AUDIENCE ? { audience: AUDIENCE } : {}) });

const stored = (): Session | null => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Session | null;
  } catch {
    return null;
  }
};

export function session(): Session | null {
  const s = stored();
  return s && s.expiresAt > Date.now() + 30_000 ? s : null;
}

/** Whether this tab holds a sign-in that can be renewed without the provider (Google mode). */
export const canRefresh = () => !!stored()?.refreshToken;

function saveTokens(t: { accessToken: string; refreshToken: string; expiresIn: number }, who?: { name: string | null; email: string | null }): Session {
  const prev = stored();
  const s: Session = { accessToken: t.accessToken, refreshToken: t.refreshToken, expiresAt: Date.now() + t.expiresIn * 1000, name: who?.name ?? prev?.name ?? null, email: who?.email ?? prev?.email ?? null };
  sessionStorage.setItem(KEY, JSON.stringify(s));
  return s;
}

let refreshing: Promise<Session | null> | null = null;
/** Google mode: renew the access token with the refresh token (one request at a time). */
function refresh(): Promise<Session | null> {
  const rt = stored()?.refreshToken;
  if (!rt) return Promise.resolve(null);
  refreshing ??= fetch(`${API}/v1/auth/refresh`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ refreshToken: rt }) })
    .then(
      async (r) => {
        if (!r.ok) {
          sessionStorage.removeItem(KEY);
          return null;
        }
        return saveTokens((await r.json()) as { accessToken: string; refreshToken: string; expiresIn: number });
      },
      () => null,
    )
    .finally(() => (refreshing = null));
  return refreshing;
}

/** A valid session, renewed if needed; null when the person has to sign in again. */
export async function ensureSession(): Promise<Session | null> {
  return session() ?? (await refresh());
}

/** Signed out on purpose in this tab: show the sign-in page instead of redirecting at once. */
export const signedOut = () => sessionStorage.getItem(SIGNED_OUT) === '1';

/**
 * Start sign-in. `mfa` asks the provider for a fresh sign-in with a second factor (step-up after
 * the API answered mfa_required); providers that enforce MFA by policy simply sign in again.
 */
export async function login(returnTo = location.pathname + location.search, opts: { mfa?: boolean } = {}): Promise<never> {
  const verifier = random(48);
  const challenge = b64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
  const state = random(16);
  const nonce = random(16);
  sessionStorage.setItem(`lumacast.pkce.${state}`, JSON.stringify({ verifier, returnTo, nonce }));
  sessionStorage.removeItem(SIGNED_OUT);
  const url = new URL((await endpoints()).authorization_endpoint);
  if (AUTH_MODE === 'google') {
    const hint = localStorage.getItem(HINT);
    url.search = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      response_type: 'code',
      redirect_uri: `${location.origin}/auth/callback`,
      scope: 'openid email profile',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
      nonce,
      // Google: pick the account last used here, or show the account chooser (Google-only prompt value).
      ...(hint ? { login_hint: hint } : GOOGLE_ISSUER === 'https://accounts.google.com' ? { prompt: 'select_account' } : {}),
    }).toString();
    location.assign(url.toString());
    return new Promise<never>(() => undefined);
  }
  url.search = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: `${location.origin}/auth/callback`,
    scope: SCOPE,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    nonce,
    ...audienceParams(),
    ...(opts.mfa ? { prompt: 'login', ...(MFA_ACR ? { acr_values: MFA_ACR } : {}) } : {}),
  }).toString();
  if (opts.mfa) sessionStorage.removeItem(KEY);
  location.assign(url.toString());
  return new Promise<never>(() => undefined);
}

const completing = new Map<string, Promise<string>>();
/**
 * Finish sign-in on /auth/callback. Calls for the same response share one attempt: React runs
 * effects twice in development, and the second call must not report "session expired".
 */
export function completeLogin(): Promise<string> {
  const key = location.search;
  let p = completing.get(key);
  if (!p) {
    p = finishLogin();
    completing.set(key, p);
  }
  return p;
}

async function finishLogin(): Promise<string> {
  const params = new URLSearchParams(location.search);
  const error = params.get('error');
  if (error) throw new Error(params.get('error_description') ?? error);
  const state = params.get('state') ?? '';
  const stored = sessionStorage.getItem(`lumacast.pkce.${state}`);
  if (!stored) throw new Error('Sign-in session expired. Please try again.');
  sessionStorage.removeItem(`lumacast.pkce.${state}`);
  const { verifier, returnTo, nonce } = JSON.parse(stored) as { verifier: string; returnTo: string; nonce?: string };
  const safeReturn = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
  if (AUTH_MODE === 'google') {
    const r = await fetch(`${API}/v1/auth/google`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code: params.get('code') ?? '', codeVerifier: verifier, redirectUri: `${location.origin}/auth/callback`, nonce }),
    });
    const body = (await r.json().catch(() => ({}))) as {
      accessToken?: string;
      refreshToken?: string;
      expiresIn?: number;
      user?: { email: string; name: string | null };
      error?: { message?: string };
    };
    if (!r.ok || !body.accessToken || !body.refreshToken) throw new Error(body.error?.message ?? 'Google sign-in failed. Please try again.');
    saveTokens({ accessToken: body.accessToken, refreshToken: body.refreshToken, expiresIn: body.expiresIn ?? 900 }, { name: body.user?.name ?? null, email: body.user?.email ?? null });
    if (body.user?.email) localStorage.setItem(HINT, body.user.email);
    return safeReturn;
  }
  const res = await fetch((await endpoints()).token_endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code', code: params.get('code') ?? '', redirect_uri: `${location.origin}/auth/callback`,
      client_id: CLIENT_ID, code_verifier: verifier, ...(RESOURCE ? { resource: RESOURCE } : {}),
    }),
  });
  const tok = (await res.json()) as { access_token?: string; id_token?: string; expires_in?: number; error_description?: string };
  if (!tok.access_token || !tok.id_token) throw new Error(tok.error_description ?? 'Token exchange failed');
  const claims = JSON.parse(atob(tok.id_token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as { name?: string; email?: string; nonce?: string };
  // The ID token is only used for display and logout hints; the API verifies the access token itself.
  if (nonce && claims.nonce !== nonce) throw new Error('Sign-in response did not match this request. Please try again.');
  const s: Session = { accessToken: tok.access_token, idToken: tok.id_token, expiresAt: Date.now() + (tok.expires_in ?? 900) * 1000, name: claims.name ?? null, email: claims.email ?? null };
  sessionStorage.setItem(KEY, JSON.stringify(s));
  return safeReturn;
}

export async function logout(): Promise<void> {
  const s = session();
  if (AUTH_MODE === 'google') {
    const rt = stored()?.refreshToken;
    sessionStorage.removeItem(KEY);
    sessionStorage.setItem(SIGNED_OUT, '1');
    if (rt) await fetch(`${API}/v1/auth/logout`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ refreshToken: rt }) }).catch(() => undefined);
    location.assign('/');
    return;
  }
  sessionStorage.removeItem(KEY);
  const end = await endpoints().then((d) => d.end_session_endpoint, () => undefined);
  if (!end) {
    location.assign('/');
    return;
  }
  const url = new URL(end);
  if (s?.idToken) url.searchParams.set('id_token_hint', s.idToken);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('post_logout_redirect_uri', `${location.origin}/`);
  location.assign(url.toString());
}

// ---- Two-step verification for LumaCast sessions (Google mode, ADR-0026) ----

async function authApi<T>(method: string, path: string, body?: unknown): Promise<T> {
  const s = await ensureSession();
  if (!s) await login();
  const r = await fetch(`${API}${path}`, {
    method,
    headers: { authorization: `Bearer ${s!.accessToken}`, ...(body ? { 'content-type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = (await r.json().catch(() => ({}))) as T & { error?: { message?: string } };
  if (!r.ok) throw new Error(json.error?.message ?? `Request failed (${r.status})`);
  return json;
}

export const twoStepStatus = () => authApi<{ enrolled: boolean; satisfied: boolean }>('GET', '/v1/auth/two-step');
export const startTwoStepEnrollment = () => authApi<{ secret: string; otpauthUri: string; qrSvg: string }>('POST', '/v1/auth/two-step/enroll', {});

/** Check a 6-digit code; on success this tab's session counts as two-step from now on. */
export async function verifyTwoStep(code: string): Promise<void> {
  saveTokens(await authApi<{ accessToken: string; refreshToken: string; expiresIn: number }>('POST', '/v1/auth/two-step/verify', { code, refreshToken: stored()?.refreshToken }));
}
